import { api } from '@shared/api';

// Ответ бэка приходит в snake_case (response-интерцептор), здесь — чистый camelCase.

export type AnalyticsPeriod = 7 | 30 | 90;

export interface AnalyticsKpis {
  usersTotal: number;
  usersNewToday: number;
  usersNewPeriod: number;
  usersNewPrev: number;
  activeToday: number;
  activeWeek: number;
  activeMonth: number;
  activePeriod: number;
  activePrev: number;
  payingPro: number;
  payingPremium: number;
  revenuePeriodRub: number;
  revenuePrevRub: number;
  /** null — учёт единиц лимита ещё не шёл. */
  unitsToday: number | null;
  unitsPeriod: number | null;
  unitsAvgDay: number | null;
  spendTodayRub: number;
  spendPeriodRub: number;
  spendPrevRub: number;
  spendForecastMonthRub: number;
  marginPeriodRub: number;
}

export interface AnalyticsDay {
  /** YYYY-MM-DD, день по Москве. */
  day: string;
  newUsers: number;
  newVerified: number;
  activeUsers: number;
  learners: number;
  creators: number;
  units: number | null;
  cardJobs: number;
  /** Генерации слов по числу вызовов модели: 1 / второй круг / третий и дальше. */
  cardJobsOne: number;
  cardJobsTwo: number;
  cardJobsThreePlus: number;
  topicJobs: number;
  /** Все расходы на модель за день, вместе с игрой и грамматикой. */
  spendRub: number;
  revenueRub: number;
  /** Из spendRub — ходы игры (и демо на лендинге) и проверка грамматики (2026-11-01). */
  gameRub: number;
  grammarRub: number;
  cardsAi: number;
  cardsManual: number;
  sessionsStarted: number;
  sessionsCompleted: number;
}

export interface AnalyticsUserDay {
  day: string;
  jobs: number;
  jobsTwo: number;
  jobsThreePlus: number;
  topicJobs: number;
  units: number | null;
  /** ₽ на слова и подборки; игра и грамматика — отдельно, в число слов не входят (2026-11-01). */
  spendRub: number;
  gameTurns: number;
  gameRub: number;
  grammarChecks: number;
  grammarRub: number;
}

export interface AnalyticsUser {
  id: string;
  name: string;
  email: string;
  plan: 'DEMO' | 'PRO' | 'PREMIUM';
  jobs: number;
  jobsRetry: number;
  topicJobs: number;
  units: number | null;
  spendRub: number;
  gameTurns: number;
  gameRub: number;
  grammarChecks: number;
  grammarRub: number;
  /** Только дни с генерациями, ходами игры, проверками или расходом лимита. */
  days: AnalyticsUserDay[];
}

export interface AdminAnalytics {
  days: AnalyticsPeriod;
  generatedAt: string;
  usageSince: string | null;
  spendSince: string | null;
  usdToRub: number;
  kpis: AnalyticsKpis;
  series: AnalyticsDay[];
  /** Demo — бесплатный тариф без срока (с 2026-10-22). */
  plans: { demo: number; pro: number; premium: number };
  funnel: { registered: number; verified: number; firstCard: number; firstSession: number; paid: number };
  retention: { nextDayCohort: number; nextDayReturned: number; weekCohort: number; weekActive: number };
  quality: {
    cardJobs: number;
    calls: number;
    extraCalls: number;
    cardsCreated: number;
    cardsFailed: number;
    topicBatches: number;
    topicFailed: number;
  };
  users: AnalyticsUser[];
}

export type Snake<T> = { [K in keyof T as K extends string ? SnakeKey<K> : never]: T[K] };
type SnakeKey<S extends string> = S extends `${infer H}${infer R}`
  ? `${H extends Lowercase<H> ? H : `_${Lowercase<H>}`}${SnakeKey<R>}`
  : S;

/** snake_case → camelCase по ключам одного уровня (значения — числа и строки). */
export function camel<T>(row: Snake<T>): T {
  return Object.fromEntries(
    Object.entries(row).map(([key, value]) => [key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()), value]),
  ) as T;
}

interface BackendAnalytics {
  days: AnalyticsPeriod;
  generated_at: string;
  usage_since: string | null;
  spend_since: string | null;
  usd_to_rub: number;
  kpis: Snake<AnalyticsKpis>;
  series: Snake<AnalyticsDay>[];
  plans: Snake<AdminAnalytics['plans']>;
  funnel: Snake<AdminAnalytics['funnel']>;
  retention: Snake<AdminAnalytics['retention']>;
  quality: Snake<AdminAnalytics['quality']>;
  users: (Snake<Omit<AnalyticsUser, 'days'>> & { days: Snake<AnalyticsUserDay>[] })[];
}

/** GET /api/v1/admin/analytics?days= — графики и KPI по дням МСК. Только для ADMIN. */
export async function fetchAdminAnalytics(days: AnalyticsPeriod): Promise<AdminAnalytics> {
  const { data } = await api.get<BackendAnalytics>('/admin/analytics', { params: { days } });
  return {
    days: data.days,
    generatedAt: data.generated_at,
    usageSince: data.usage_since,
    spendSince: data.spend_since,
    usdToRub: data.usd_to_rub,
    kpis: camel<AnalyticsKpis>(data.kpis),
    series: data.series.map((row) => camel<AnalyticsDay>(row)),
    plans: camel<AdminAnalytics['plans']>(data.plans),
    funnel: camel<AdminAnalytics['funnel']>(data.funnel),
    retention: camel<AdminAnalytics['retention']>(data.retention),
    quality: camel<AdminAnalytics['quality']>(data.quality),
    users: data.users.map(({ days: cells, ...user }) => ({
      ...camel<Omit<AnalyticsUser, 'days'>>(user),
      days: cells.map((cell) => camel<AnalyticsUserDay>(cell)),
    })),
  };
}
